import React, { useCallback, useMemo, useState } from "react";
import { mapCategoryName, TransactionDirectionType } from "@common/categories";
import { Category } from "@common/types/category";
import type { TooltipTriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import { PlainModal } from "@design-system/Modal/Modal";
import { CategoryIcon } from "@components/transactions/CategoryIcon";
import { Markdown } from "@design-system/Markdown/Markdown";
import {
    Picker,
    pickerTreeRowPlacements,
    type PickerCellEditor,
} from "@design-system/Picker/Picker.lib";
import { CreateCategoryModal } from "@components/settings/Categories/CreateCategoryModal";
import { useCategoriesTree } from "@hooks/useCategoriesTree";
import { useIsCategoryGlMappingEnabled } from "@hooks/useIsCategoryGlMappingEnabled";
import { PlusIcon } from "@icons";
import { HStack } from "@design-system/HStack/HStack";
import { buildCategorySearchLabels } from "./buildCategorySearchLabels";
import { ComboboxTriggerButton } from "./ComboboxTriggerButton";

interface Props {
    categories?: Category[];
    selectedCategory?: Pick<
        Category,
        "id" | "label" | "taxonomy" | "parentCategoryId" | "workspaceId"
    >;
    onChange: (category: Category | undefined) => void;
    disabled?: boolean;
    placeholder?: string;
    readOnly?: boolean;
    hideArrow?: boolean;
    className?: string;
    triggerButtonProps?: TooltipTriggerButtonProps;
    onOpenChange?: (open: boolean) => void;
    onCreationModalOpen?: () => void;
    onCreationModalClose?: () => void;
    hideFooter?: boolean;
    open?: boolean;
    /** A Picker trigger part (e.g. `Picker.CellContent`) replacing the default button. */
    trigger?: React.ReactNode;
    suggestedCategories?: string[];
    defaultParentCategoryId?: string;
    /** Spreadsheet grid editors: the opening key seeds the search and Tab picks the highlighted row. */
    cellEditor?: PickerCellEditor;
}

const flattenCategories = (categories: Category[]) => {
    const seenCategoryIds = new Set<string>();

    return categories
        .flatMap((category) => [category, ...(category.subcategories ?? [])])
        .filter((category) => {
            if (seenCategoryIds.has(category.id)) {
                return false;
            }

            seenCategoryIds.add(category.id);
            return true;
        });
};

const categoryName = (
    category: Pick<
        Category,
        "id" | "label" | "taxonomy" | "parentCategoryId" | "workspaceId"
    >,
) =>
    mapCategoryName({
        category,
        transactionDirection: TransactionDirectionType.mixed,
    });

const CategoryRows = ({ categories }: { categories: Category[] }) => {
    const placements = useMemo(
        () =>
            pickerTreeRowPlacements(
                categories.map((category) =>
                    category.parentCategoryId == null ? 0 : 1,
                ),
            ),
        [categories],
    );

    return categories.map((category, index) => {
        const label = categoryName(category);
        return (
            <Picker.Item
                key={category.id}
                value={category.id}
                depth={category.parentCategoryId == null ? 0 : 1}
                treePosition={placements[index].treePosition}
                leading={
                    category.parentCategoryId == null ? (
                        <CategoryIcon categoryId={category.id} />
                    ) : undefined
                }
                tooltip={
                    category.description ? (
                        <Markdown>{category.description}</Markdown>
                    ) : undefined
                }
            >
                {label}
            </Picker.Item>
        );
    });
};

export const CategoryCombobox: React.FC<Props> = ({
    categories: categoriesProp,
    selectedCategory,
    onChange,
    disabled,
    placeholder = "Select category",
    readOnly,
    hideArrow,
    className,
    triggerButtonProps,
    onOpenChange,
    onCreationModalOpen,
    onCreationModalClose,
    hideFooter,
    open,
    trigger,
    suggestedCategories,
    defaultParentCategoryId,
    cellEditor,
}) => {
    const categoriesTree = useCategoriesTree();
    const isGlMappingEnabled = useIsCategoryGlMappingEnabled();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [showCreationModal, setShowCreationModal] = useState(false);
    const [typedSearch, setTypedSearch] = useState<string>();
    const [creationName, setCreationName] = useState<string>();

    const categories = useMemo(
        () => flattenCategories(categoriesProp ?? categoriesTree),
        [categoriesProp, categoriesTree],
    );
    const categoriesById = useMemo(
        () => new Map(categories.map((category) => [category.id, category])),
        [categories],
    );
    const selectedCategoryId =
        selectedCategory && categoriesById.has(selectedCategory.id)
            ? selectedCategory.id
            : null;
    const categorySearchLabels = useMemo(
        () =>
            buildCategorySearchLabels(categories, (category) =>
                categoryName(category).toLowerCase(),
            ),
        [categories],
    );

    const filteredCategories = useMemo(() => {
        const normalizedSearch = typedSearch?.trim().toLowerCase();
        if (!normalizedSearch) {
            return categories;
        }

        return categories.filter((category) => {
            const searchLabel = categorySearchLabels.get(category.id) ?? "";
            return searchLabel.includes(normalizedSearch);
        });
    }, [categories, typedSearch, categorySearchLabels]);
    const suggestedCategoryIds = useMemo(() => {
        if (!suggestedCategories?.length) {
            return new Set<string>();
        }
        const suggestedTaxonomies = new Set<string>(suggestedCategories);

        return new Set(
            categories
                .filter(
                    (category) =>
                        category.taxonomy != null &&
                        suggestedTaxonomies.has(category.taxonomy),
                )
                .map((category) => category.id),
        );
    }, [categories, suggestedCategories]);
    const suggestedCategoryGroup = useMemo(
        () =>
            filteredCategories.filter((category) =>
                suggestedCategoryIds.has(category.id),
            ),
        [filteredCategories, suggestedCategoryIds],
    );
    const mainCategoryGroup = useMemo(
        () =>
            filteredCategories.filter(
                (category) => !suggestedCategoryIds.has(category.id),
            ),
        [filteredCategories, suggestedCategoryIds],
    );

    const onCreateNew = useCallback(() => {
        setCreationName(typedSearch);
        setIsDropdownOpen(false);
        setShowCreationModal(true);
        onCreationModalOpen?.();
    }, [onCreationModalOpen, typedSearch]);

    const onCreationModalClosed = useCallback(
        (createdCategory?: Category) => {
            setShowCreationModal(false);
            if (createdCategory) {
                onChange(createdCategory);
            }
            onCreationModalClose?.();
        },
        [onChange, onCreationModalClose],
    );

    return (
        <>
            <Picker.Select
                searchable
                autoHighlight
                cellEditor={cellEditor}
                value={selectedCategoryId}
                onValueChange={(categoryId) =>
                    onChange(
                        categoryId === null
                            ? undefined
                            : categoriesById.get(categoryId),
                    )
                }
                onInputValueChange={(inputValue) => {
                    setTypedSearch(inputValue || undefined);
                }}
                onOpenChange={(isOpen) => {
                    setIsDropdownOpen(isOpen);
                    onOpenChange?.(isOpen);
                }}
                disabled={disabled || readOnly}
                open={open ?? isDropdownOpen}
            >
                {trigger ?? (
                    <ComboboxTriggerButton
                        buttonProps={triggerButtonProps}
                        className={className}
                        ariaLabel={
                            selectedCategory
                                ? categoryName(selectedCategory)
                                : undefined
                        }
                        value={
                            selectedCategory ? (
                                <HStack gap={4} align="center">
                                    <CategoryIcon
                                        categoryId={selectedCategory.id}
                                    />
                                    {categoryName(selectedCategory)}
                                </HStack>
                            ) : undefined
                        }
                        placeholder={placeholder}
                        hideArrow={hideArrow}
                    />
                )}
                <Picker.Popup sideOffset={4}>
                    <Picker.Search placeholder="Search categories..." />
                    <Picker.List>
                        {suggestedCategoryGroup.length > 0 && (
                            <Picker.Section>
                                <Picker.SectionTitle>
                                    Suggested
                                </Picker.SectionTitle>
                                <CategoryRows
                                    categories={suggestedCategoryGroup}
                                />
                            </Picker.Section>
                        )}
                        {suggestedCategoryGroup.length > 0 &&
                            mainCategoryGroup.length > 0 && (
                                <Picker.Separator />
                            )}
                        <CategoryRows categories={mainCategoryGroup} />
                        {filteredCategories.length === 0 && (
                            <Picker.Empty>No matching categories</Picker.Empty>
                        )}
                        {!hideFooter && (
                            <Picker.Footer>
                                <Picker.Item
                                    leading={<PlusIcon />}
                                    onSelect={onCreateNew}
                                >
                                    {typedSearch
                                        ? `Create "${typedSearch}"`
                                        : "Create new category"}
                                </Picker.Item>
                            </Picker.Footer>
                        )}
                    </Picker.List>
                </Picker.Popup>
            </Picker.Select>
            <PlainModal
                show={showCreationModal}
                onHide={() => onCreationModalClosed()}
                size={isGlMappingEnabled ? "lg" : "md"}
            >
                <CreateCategoryModal
                    categoryName={creationName}
                    parentCategoryId={defaultParentCategoryId}
                    onCategoryCreated={(category) =>
                        onCreationModalClosed(category)
                    }
                    onHide={() => onCreationModalClosed()}
                />
            </PlainModal>
        </>
    );
};
